import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import CardHeader from '@mui/material/CardHeader';

import { useMemo } from 'react';
import { CardActions } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SettingsHeader } from './SettingsHeader';
import { SettingsWrapper } from '../../../layout';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getTokenDetails } from '../../../store/authenticate/selectors';
import { getMembers } from '../../../store/team/selectors';

const BillingSettings: React.FC = () => {
  const tokenDetails = useAppSelector(getTokenDetails);
  const members = useAppSelector(getMembers);
  const { t } = useTranslation(['settings', 'team', 'pricing']);
  const navigate = useNavigate();

  const paymentText = useMemo(() => {
    if (tokenDetails.subscription.inTrial) {
      return t('freeTrial');
    }

    if (tokenDetails.subscription?.subscriptionPlanName === 'free') {
      return t('personalFree', { ns: 'pricing' });
    }

    const price = tokenDetails.subscription?.subscriptionPlanName === 'lite' ? 1.99 : 3.99;

    const payment = (members.length * price).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${payment}$ - (${members.length}) ${t('members', { ns: 'team' })}`;
  }, [members, tokenDetails]);

  const onPlanChange = () => {
    navigate('/onboarding/pricing?channel=app');
  };

  return (
    <SettingsWrapper>
      <SettingsHeader
        title={t('billing')}
        subtitle={t('manageYourBilling')}
      />
      <Divider sx={{
        pt: 4,
        mr: 4,
        borderStyle: 'dashed',
      }}
      />
      <Grid
        sx={{
          py: 4,
          paddingTop: { xs: 2 },
        }}
        spacing={2}
        columns={12}
        container
      >
        <Grid size={{
          lg: 8,
          md: 7,
          xs: 12,
          sm: 12,
        }}
        >
          <Card sx={{
            mt: 6,
            marginTop: { xs: 2 },
            borderRadius: 2,
            ':hover': {
              boxShadow: 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px',
            },
          }}
          >
            <CardHeader
              sx={{
                p: 2,
              }}
              title={t('yourPlan')}
              titleTypographyProps={{
                variant: 'subtitle1',
              }}
            />
            <Divider sx={{ borderStyle: 'dashed' }} />
            <Card
              sx={{
                p: 2.5,
                m: 4,
                width: 1,
                maxWidth: 300,
                boxShadow: 0,
                bgcolor: 'background.default',
                border: (theme) => `solid 1px ${theme.palette.divider}`,
              }}
            >
              <Stack spacing={0.5} direction="row" alignItems="center" sx={{ top: 8, right: 8, position: 'absolute' }}>
                <Typography pt={2} color="#A855F7" variant="subtitle2">
                  {tokenDetails.subscription?.subscriptionPlanName === 'lite' ? 1.99 : 3.99}
                  $
                </Typography>
                <Typography pt={2} variant="caption">
                  (
                  {t('perTeamMember')}
                  )
                </Typography>
              </Stack>

              <Box
                component="img"
                src="/icons/ic_layers.svg"
                sx={{ width: 40, height: 40 }}
              />

              <Typography pt={2} variant="subtitle2">{tokenDetails.subscription.subscriptionPlanName?.toUpperCase()}</Typography>
            </Card>
            <CardActions>
              <Stack m={2} ml="auto">
                <Button onClick={onPlanChange} size="small" variant="outlined">
                  {t('upgradePlan')}
                </Button>
              </Stack>
            </CardActions>
          </Card>
        </Grid>
        <Grid size={{
          lg: 4,
          md: 5,
          xs: 12,
          sm: 12,
        }}
        >
          <Card sx={{
            mt: 6,
            minHeight: 400,
            marginTop: { xs: 2 },
            borderRadius: 2,
            ':hover': {
              boxShadow: 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px',
            },
          }}
          >
            <CardHeader
              sx={{
                p: 2,
              }}
              title={t('invoiceHistory')}
              titleTypographyProps={{
                variant: 'subtitle1',
              }}
            />
            <Divider sx={{ borderStyle: 'dashed' }} />
            <Stack
              spacing={3}
              sx={{
                p: 2,
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ width: 1 }}>
                <Typography variant="overline">{t('nextPayment')}</Typography>
                <Typography color="#A855F7" variant="subtitle2">
                  {paymentText}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </SettingsWrapper>
  );
};

export {
  BillingSettings,
};
